/**
 * Where a proposed fact would be filed if accepted, worked out from its concept
 * ids and named rival against the live orbit. Presentation only: the backend
 * decides the real filing on accept, so this is the review pane's best reading
 * of it (Universe / Proposal path, Stage · Lands here, "Charts an empty group").
 */
import type { FactProposal } from "@/types/chat.type";
import type { Ring, RingArc } from "./universeLayout";

export type ProposalPlacement = {
  /** The arc the fact lands on, when the orbit has one for it. */
  arcKey: string | null;
  /** Subject › rival › group, e.g. ["Competitors", "Shahiya", "Where they show up"]. */
  path: string[];
  /** The group holds no facts yet, so accepting charts it. */
  chartsEmpty: boolean;
};

const same = (a: string | undefined | null, b: string | undefined | null) =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

export function placeProposal(
  proposal: Pick<FactProposal, "conceptIds" | "competitor">,
  rings: Ring[],
  conceptNameById: Map<string, string>,
): ProposalPlacement {
  const names = proposal.conceptIds.map((id) => conceptNameById.get(id)).filter((n): n is string => !!n);
  const rival = proposal.competitor?.trim() || null;

  const candidates: { ring: Ring; arc: RingArc }[] = [];
  for (const ring of rings) for (const arc of ring.arcs) candidates.push({ ring, arc });

  const byConcept = (c: { arc: RingArc }) => names.some((n) => same(n, c.arc.concept));
  let hit: { ring: Ring; arc: RingArc } | undefined;
  if (rival) {
    const rivalArcs = candidates.filter((c) => same(c.arc.entityName, rival));
    hit = rivalArcs.find(byConcept) ?? rivalArcs.find((c) => !c.arc.concept);
    if (!hit) {
      // A rival the roster does not know yet: the path still names it.
      const ring = rings.find((r) => r.arcs.some((a) => a.entityName));
      return { arcKey: null, path: [ring?.label ?? "Competitors", rival], chartsEmpty: false };
    }
  } else {
    hit = candidates.find((c) => !c.arc.entityName && byConcept(c));
  }

  if (hit) {
    const { ring, arc } = hit;
    const path = [ring.label, ...(arc.entityName ? [arc.entityName] : []), arc.label];
    return { arcKey: arc.key, path, chartsEmpty: !arc.charted };
  }

  const firstId = proposal.conceptIds[0];
  const ring = firstId ? rings.find((r) => r.conceptIds.includes(firstId)) : undefined;
  const concept = firstId ? conceptNameById.get(firstId) : undefined;
  if (ring) return { arcKey: null, path: concept ? [ring.label, concept] : [ring.label], chartsEmpty: false };
  return { arcKey: null, path: concept ? [concept] : ["Unsorted"], chartsEmpty: false };
}
